import { PostPreview } from '../Previews/PostPreview'
import Styles from './PostPreviewBody.module.scss'
import MobileStyles from './MobilePostPreviewBody.module.scss'
import {
  ArticlePostPreviewActionTypes,
  maxCharacters,
  maxFiles,
  MediaFileMetadata,
  providerFromChannelType,
  useArticlePostPreviewContext,
} from '../ArticlePostPreviewContext'
import { usePostProperties } from '../usePostProperties'
import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ImageAdd } from 'blaze-ui/icons'
import { PreviewInput } from './PreviewInput'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { EditOption } from './EditOption/EditOption'
import { PostImages } from './PostImages/PostImages'
import { MediaObject, SelectedImage } from 'types'
import { Error } from './Error'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { Provider } from 'components/ImagePickerModal/Types'
import { observer } from 'mobx-react-lite'
import cx from 'classnames'
import { AlternativeOptions } from './AlternativeOptions/AlternativeOptions'
import { ImagePickerDrawer } from 'components/ImagePickerModal/ImagePickerDrawer/ImagePickerDrawer'
import { EditingTopBar } from './EditingTopBar'
import { Actions } from './Actions/Actions'
import { useRootStore } from 'stores/RootStore'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { noop } from 'lodash'
import { useMediaFileUploadProgress } from '../useMediaFileUploadProgress'
import { useEffect, useRef } from 'react'
import { PollScheduledPostStatus } from './PollScheduledPostStatus/PollScheduledPostStatus'
import { ImagePickerContextState } from 'components/ImagePickerModal/ImagePickerContextTypes'
import {
  useCreateMediaObjects,
  useCreateMediaObjectsWithoutPresigning,
} from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { ValidationBanner } from 'components/ValidationBanner/ValidationBanner'
import { useErrorAction } from './hooks'
import { ScheduledPostErrorType } from 'api/clients/ScheduledPostsClient'
import { usePostValidation } from './usePostValidation'

export const MobilePostPreviewBody = observer(() => {
  const {
    state: {
      content,
      channel,
      files,
      errorMessage,
      errorType,
      editing,
      changingCopy,
      loading,
      uploadingMediaFilesMetadata,
    },
    validationErrors,
    almanacFile,
    dispatch,
    scheduledPost,
    updateContent,
    canEdit,
  } = useArticlePostPreviewContext()
  const { drawersStore } = useRootStore()
  const { onUploadStart, onUploadEnd, onUploadProgress } = useMediaFileUploadProgress(drawersStore)
  const workspaceId = useCurrentWorkspaceId()
  const { status } = usePostProperties({ id: almanacFile.id, type: AssigneeType.Article })
  const integration = scheduledPost?.external_integration
  const provider = integration?.provider || providerFromChannelType[channel as ChannelType]
  const maxFilesToUpload = provider ? maxFiles[provider] : 0
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const metadataRef = useRef<MediaFileMetadata[]>([])
  const openWindowSafely = useOpenWindowSafely()
  const { errorAction } = useErrorAction({ integration, errorType })
  const { mutateAsync: createMediaObjectsWithoutPresigning } = useCreateMediaObjectsWithoutPresigning()

  const addFilesToPreview = async (selectedUploadFiles: SelectedImage[], context: ImagePickerContextState) => {
    const maxFilesCount = integration ? maxFiles[integration.provider] : Infinity
    let newFiles
    if (!context.provider || context.provider === 'uploads' || context.provider === 'ai') {
      // provider is undefined when uploading from device
      newFiles = await createMediaObjectsWithoutPresigning(
        selectedUploadFiles.map(({ id, ...file }, index) => ({
          ...file,
          ...metadataRef.current[index],
          url: file.mime_type?.startsWith('video') && file.source_url ? file.source_url : file.url,
        })),
      )
    } else {
      newFiles = selectedUploadFiles.map((file) => ({
        id: parseInt(file.id as string),
        source_url: file.url,
        mime_type: file.mime_type as string,
      })) as MediaObject[]
    }

    let metadatas = [...metadataRef.current]
    const newFilesUpdated = newFiles.map((file) => {
      const metadata = metadatas.shift()

      return {
        ...file,
        ...metadata,
      }
    })

    dispatch({
      type: ArticlePostPreviewActionTypes.SET_FILES,
      payload: [...files, ...newFilesUpdated].slice(0, maxFilesCount),
    })
    onUploadEnd()
    drawersStore.closeDrawer()
  }

  // Need to keep the reference to the metadata array from the article post preview context and pass that
  // to the MediaUploadModal's handleSubmit callback, otherwise the value will be stale
  useEffect(() => {
    metadataRef.current = uploadingMediaFilesMetadata
  }, [uploadingMediaFilesMetadata])
  const { mutateAsync: createMediaObjects } = useCreateMediaObjects()

  const { uploadImageFiles } = useImportMediaFiles(false, noop, onUploadProgress)

  const uploadImages = async (files: File[], context: ImagePickerContextState) => {
    let objects: MediaObject[] = []
    if (!context.provider) {
      // provider is undefined when uploading from device
      const uploadedImages = await uploadImageFiles(files, uploadsProjectId)
      if (!uploadedImages) return []
      return uploadedImages.map(
        (file) => ({ id: file.id, url: file.source_url, mime_type: file.mime_type } as SelectedImage),
      )
    } else {
      objects = await createMediaObjects(files.map((file) => ({ file, filename: file.name })))
    }

    return objects.map(
      (file) =>
        ({
          id: file.id.toString(),
          url: file.source_url,
          mime_type: file.mime_type,
          original_filename: file.id.toString(),
        } as SelectedImage),
    )
  }

  const openMediaDrawer = () => {
    drawersStore.openDrawer(ImagePickerDrawer, {
      uploadImages,
      maxCount: maxFilesToUpload,
      onConfirm: addFilesToPreview,
      hiddenProviders: new Set<Provider>(['gifs']),
      accept: ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES,
      onUploadStart,
      insertToEditor: false,
    })
  }

  const isMediaError = errorType === ScheduledPostErrorType.MediaFormatError
  const postingFailed = status?.value === PostStatusPropertyValue.Failed
  const errors = errorMessage && postingFailed && isMediaError ? [errorMessage, ...validationErrors] : validationErrors

  const { characterLimitErrors, otherErrors, selectedPlatform } = usePostValidation({
    errors,
    provider,
  })

  return (
    <div className={Styles.previewBody}>
      {editing && <EditingTopBar />}
      {scheduledPost && <PollScheduledPostStatus scheduledPost={scheduledPost} />}

      {!loading && (
        <>
          {errorMessage && postingFailed && !isMediaError && (
            <Error errorMessages={[errorMessage]} action={errorAction()} />
          )}
          {characterLimitErrors.length > 0 && selectedPlatform && (
            <ValidationBanner content={content} selectedPlatform={selectedPlatform} />
          )}
          {otherErrors.length > 0 && (
            <Error
              errorMessages={otherErrors}
              action={{
                label: 'See Requirements',
                onPress: () =>
                  openWindowSafely(
                    'https://help.blaze.ai/en/articles/9911718-image-and-video-file-requirements-by-platform-connection',
                  ),
              }}
            />
          )}
        </>
      )}

      <div className={cx(Styles.previewBodyContent, { [Styles.editing]: editing })}>
        <div className={Styles.previewSection}>
          {editing ? (
            <div className={Styles.previewBodyLeft}>
              <PreviewInput
                readOnly={!editing}
                value={content}
                onChange={(e, shouldPersistChange) => {
                  dispatch({ type: ArticlePostPreviewActionTypes.SET_CONTENT, payload: e.target.value })
                  if (shouldPersistChange) updateContent(e.target.value)
                }}
                maxCharacterCount={provider ? maxCharacters[provider] : undefined}
              />
            </div>
          ) : (
            <>
              {changingCopy ? (
                <AlternativeOptions content={content} />
              ) : (
                <PostPreview
                  content={content}
                  channel={channel}
                  integration={integration}
                  mediaObjects={files}
                  loading={loading}
                  openFileUploadPrompt={openMediaDrawer}
                  hideConnectButton={!canEdit}
                />
              )}
            </>
          )}
        </div>
        {editing && (
          <>
            {(files.length > 0 || uploadingMediaFilesMetadata.length > 0) && (
              <PostImages
                files={files}
                provider={provider}
                onConfirm={addFilesToPreview}
                uploadImages={uploadImages}
                onRemove={(index) =>
                  dispatch({
                    type: ArticlePostPreviewActionTypes.SET_FILES,
                    payload: files.filter((_, i) => i !== index),
                  })
                }
              />
            )}
            {files.length === 0 && uploadingMediaFilesMetadata.length === 0 && (
              <div className={MobileStyles.mobileEditOptions}>
                <EditOption icon={<ImageAdd />} name="Add Media" onClick={openMediaDrawer} />
              </div>
            )}
          </>
        )}
        {!editing && !changingCopy && <Actions />}
      </div>
    </div>
  )
})
